import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AddressApi, OrderApi, CouponApi, TicketApi, SupportApi, MetaApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useContent } from '../ContentContext';
import { PageHero } from '../components/Common';
import AddressForm from '../components/AddressForm';

// Social icons for the Help & Support contact block; links come from admin.
const SOCIAL_ICONS = {
  instagram: 'fa-instagram',
  facebook: 'fa-facebook-f',
  youtube: 'fa-youtube',
  twitter: 'fa-x-twitter',
  linkedin: 'fa-linkedin-in',
};
const SOCIAL_ORDER = ['instagram', 'facebook', 'youtube', 'twitter', 'linkedin'];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const TICKET_CATEGORIES = ['order', 'bulk', 'payment', 'product', 'delivery', 'account', 'other'];

const BULK_DRAFT = {
  category: 'bulk',
  subject: 'Bulk order enquiry',
  message: 'I would like a quote for a bulk order.\n\nProduct:\nPack size:\nQuantity:\nDelivery city:',
};

const DEALERSHIP_DRAFT = {
  category: 'dealership',
  subject: 'Dealership enquiry',
  message: 'I am interested in a Golden Taj dealership.\n\nFirm name:\nCity / area:\nCurrent business:\nMonthly volume:',
};

// Config for the two enquiry tabs (bulk / dealership), which share a form.
const ENQUIRY_TABS = {
  bulk: {
    draft: BULK_DRAFT,
    title: 'Bulk Order Enquiry',
    icon: 'fa-truck-fast',
    heading: 'Ordering in bulk?',
    blurb: 'Get a custom quote for large quantities, catering or resale.',
    cta: 'Submit Enquiry',
  },
  dealership: {
    draft: DEALERSHIP_DRAFT,
    title: 'Dealership Enquiry',
    icon: 'fa-handshake',
    heading: 'Partner with us',
    blurb: 'Become a Golden Taj distributor or dealer in your area.',
    cta: 'Submit Enquiry',
  },
};

// Help & Support: FAQs (from admin), contact details, social links, and the
// customer's support tickets — mirroring the app's Help & Support screen.
function TicketsTab() {
  const { company } = useContent();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = list view
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [social, setSocial] = useState({});

  const load = () => TicketApi.list().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  // FAQs are managed from admin (support bot); social links from admin Settings.
  useEffect(() => {
    SupportApi.bot().then((r) => setFaqs((r.bot?.faqs || []).filter((f) => f.q && f.a))).catch(() => {});
    MetaApi.config().then((d) => setSocial(d.social || {})).catch(() => {});
  }, []);

  const socialLinks = SOCIAL_ORDER.map((k) => [k, social[k]]).filter(([, url]) => url);
  const phones = company.phones || [];

  const submit = async () => {
    setMsg('');
    if (!form.subject.trim() || !form.message.trim()) {
      setMsg('Please add a subject and describe your requirement.');
      return;
    }
    setBusy(true);
    try {
      await TicketApi.create({ ...form, priority: 'medium' });
      setForm(null);
      load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (form) {
    return (
      <div className="panel-card">
        <div className="row-between">
          <h3>{form.category === 'bulk' ? 'Bulk Order Enquiry' : 'New Ticket'}</h3>
          <button className="link-btn inline" onClick={() => { setForm(null); setMsg(''); }}>Cancel</button>
        </div>

        <div className="form-row">
          <div className="field">
            <label>Subject *</label>
            <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          </div>
          <div className="field">
            <label>Category</label>
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c === 'bulk' ? 'Bulk Order' : c[0].toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Message *</label>
          <textarea rows={6} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
        </div>

        {msg ? <p className="form-msg err">{msg}</p> : null}
        <button className="consult-btn" style={{ justifyContent: 'center' }} disabled={busy} onClick={submit}>
          {busy ? 'Sending…' : 'Submit'}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* FAQs — managed from admin */}
      {faqs.length ? (
        <div className="panel-card">
          <h3>Frequently Asked Questions</h3>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <div className={`faq-item${openFaq === i ? ' open' : ''}`} key={i}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <i className={`fas fa-angle-${openFaq === i ? 'up' : 'down'}`} />
                </button>
                {openFaq === i ? <p className="faq-a">{f.a}</p> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Contact details + social */}
      <div className="panel-card">
        <h3>Contact Us</h3>
        <div className="contact-list">
          {phones.map((p) => {
            const local = String(p).replace(/\D/g, '').slice(-10);
            return (
              <div className="contact-line" key={p}>
                <span className="contact-ic"><i className="fas fa-phone" /></span>
                <div className="contact-body">
                  <span className="muted small">Call us</span>
                  <a href={`tel:+91${local}`}>+91 {local}</a>
                </div>
                {company.whatsapp ? (
                  <a
                    className="contact-wa"
                    href={`https://wa.me/${company.whatsapp}${company.whatsappText ? `?text=${encodeURIComponent(company.whatsappText)}` : ''}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fab fa-whatsapp" /> WhatsApp
                  </a>
                ) : null}
              </div>
            );
          })}
          {company.email ? (
            <div className="contact-line">
              <span className="contact-ic"><i className="fas fa-envelope" /></span>
              <div className="contact-body">
                <span className="muted small">Email us</span>
                <a href={`mailto:${company.email}`}>{company.email}</a>
              </div>
            </div>
          ) : null}
          {company.address ? (
            <div className="contact-line">
              <span className="contact-ic"><i className="fas fa-location-dot" /></span>
              <div className="contact-body">
                <span className="muted small">Visit us</span>
                <span>{company.address}</span>
              </div>
            </div>
          ) : null}
        </div>

        {socialLinks.length ? (
          <>
            <p className="muted small" style={{ marginTop: 16, marginBottom: 8 }}>Follow us</p>
            <div className="contact-social">
              {socialLinks.map(([key, url]) => (
                <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={key}>
                  <i className={`fab ${SOCIAL_ICONS[key]}`} />
                </a>
              ))}
            </div>
          </>
        ) : null}
      </div>

      {/* Support tickets */}
      <div className="panel-card">
        <div className="row-between">
          <h3>Support Tickets</h3>
          <button className="link-btn inline" onClick={() => setForm({ category: 'order', subject: '', message: '' })}>
            + New ticket
          </button>
        </div>

        {items === null ? (
          <p className="muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="muted">No tickets yet. Raise one and our team will help you out.</p>
        ) : (
          items.map((t) => (
            <div className="order-row" key={t._id}>
              <div>
                <strong>{t.ticketNumber}</strong>
                <p className="muted small">{t.subject}</p>
                <span className={`status ${t.status}`}>{String(t.status).replace(/_/g, ' ')}</span>
              </div>
              <span className="muted small">{fmtDate(t.updatedAt)}</span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// Bulk-order / dealership enquiry — submits a support ticket in the matching
// category. `kind` selects which enquiry (see ENQUIRY_TABS).
function EnquiryTab({ kind }) {
  const cfg = ENQUIRY_TABS[kind];
  const [form, setForm] = useState({ ...cfg.draft });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState(false);

  // Reset the form whenever the visitor switches between the two enquiry tabs.
  useEffect(() => { setForm({ ...cfg.draft }); setDone(false); setMsg(''); }, [kind, cfg.draft]);

  const submit = async () => {
    setMsg('');
    if (!form.subject.trim() || !form.message.trim()) {
      setMsg('Please add a subject and describe your requirement.');
      return;
    }
    setBusy(true);
    try {
      await TicketApi.create({ ...form, category: cfg.draft.category, priority: 'medium' });
      setDone(true);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="panel-card">
        <h3>{cfg.title}</h3>
        <p className="form-msg ok"><i className="fas fa-circle-check" /> Thanks! Our team will get back to you shortly.</p>
        <button className="link-btn inline" onClick={() => { setForm({ ...cfg.draft }); setDone(false); }}>Send another</button>
      </div>
    );
  }

  return (
    <div className="panel-card">
      <div className="bulk-card" style={{ marginBottom: 18 }}>
        <div className="bulk-icon"><i className={`fas ${cfg.icon}`} /></div>
        <div style={{ flex: 1 }}>
          <strong>{cfg.heading}</strong>
          <p className="muted small">{cfg.blurb}</p>
        </div>
      </div>

      <div className="field">
        <label>Subject *</label>
        <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
      </div>
      <div className="field">
        <label>Your requirement *</label>
        <textarea rows={7} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
      </div>

      {msg ? <p className="form-msg err">{msg}</p> : null}
      <button className="consult-btn" style={{ justifyContent: 'center' }} disabled={busy} onClick={submit}>
        {busy ? 'Sending…' : cfg.cta}
      </button>
    </div>
  );
}

// Coupons the admin has published, with their conditions.
function CouponsTab() {
  const [items, setItems] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    CouponApi.public().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 1600);
  };

  const offerText = (c) =>
    c.type === 'percent'
      ? `${c.value}% OFF${c.maxDiscount ? ` up to ₹${c.maxDiscount}` : ''}`
      : `₹${c.value} OFF`;

  if (!items) return <div className="panel-card"><p className="muted">Loading coupons…</p></div>;

  return (
    <div className="panel-card">
      <h3>Coupons &amp; Offers</h3>
      {items.length === 0 ? (
        <p className="muted">No coupons right now — check back soon.</p>
      ) : (
        <div className="coupon-list">
          {items.map((c) => (
            <div className={`coupon-ticket ${c.used ? 'used' : ''}`} key={c.code}>
              <div className="ticket-ribbon">
                <i className="fas fa-tag" />
                <span>{offerText(c)}</span>
              </div>
              <div className="ticket-body">
                <div className="row-between">
                  <strong className="ticket-code">{c.code}</strong>
                  {c.used ? (
                    <span className="muted small">Already used</span>
                  ) : (
                    <button className="ticket-copy" onClick={() => copy(c.code)}>
                      {copied === c.code ? 'Copied!' : 'COPY'}
                    </button>
                  )}
                </div>
                {c.description ? <p className="muted small">{c.description}</p> : null}
                <ul className="ticket-terms">
                  <li>{c.minOrder > 0 ? `Min. order ₹${c.minOrder}` : 'No minimum order'}</li>
                  {c.oncePerUser ? <li>One use per customer</li> : null}
                  {c.terms ? <li>{c.terms}</li> : null}
                  {c.validTo ? <li>Valid till {fmtDate(c.validTo)}</li> : null}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="muted small" style={{ marginTop: 12 }}>Apply your coupon in the cart at checkout.</p>
    </div>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, isLoggedIn, booting, logout, updateProfile } = useAuth();
  const { count: wishCount } = useWishlist();

  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // New sign-ups land here to complete their profile.
  const [profile, setProfile] = useState({ firstName: '', lastName: '', email: '', gender: '' });
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (!booting && !isLoggedIn) navigate('/login', { replace: true });
  }, [booting, isLoggedIn, navigate]);

  useEffect(() => {
    if (params.get('complete') === '1') setTab('profile');
  }, [params]);

  useEffect(() => {
    if (!user) return;
    // Fall back to splitting an existing display name for older accounts.
    const [first = '', ...rest] = (user.name || '').trim().split(/\s+/);
    setProfile({
      firstName: user.firstName || first,
      lastName: user.lastName || rest.join(' '),
      email: user.email || '',
      gender: user.gender || '',
    });
  }, [user]);

  // A profile is incomplete until we have a name, an email and a gender.
  const incomplete = !user?.name || !user?.email || !user?.gender;

  const loadAddresses = useCallback(
    () => AddressApi.list().then((d) => setAddresses(d.items || [])).catch(() => {}),
    []
  );

  useEffect(() => {
    if (!isLoggedIn) return;
    OrderApi.list().then((d) => setOrders(d.items || [])).catch(() => {});
    loadAddresses();
  }, [isLoggedIn, loadAddresses]);

  const saveProfile = async () => {
    setSaved('');
    if (!profile.firstName.trim()) return setSaved('Please enter your first name');
    if (!profile.lastName.trim()) return setSaved('Please enter your last name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email.trim())) return setSaved('Please enter a valid email');
    if (!profile.gender) return setSaved('Please select your gender');

    try {
      await updateProfile({
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        name: `${profile.firstName.trim()} ${profile.lastName.trim()}`,
        email: profile.email.trim(),
        gender: profile.gender,
      });
      setSaved('Profile updated');
      setTimeout(() => setSaved(''), 2500);
    } catch (e) { setSaved(e.message); }
  };

  const removeAddress = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await AddressApi.remove(id);
    loadAddresses();
  };

  if (booting || !user) {
    return <><PageHero title="My Account" /><section><div className="container"><p className="muted">Loading…</p></div></section></>;
  }

  return (
    <>
      <PageHero title="My Account" />

      <section>
        <div className="container account-layout">
          {/* Sidebar */}
          <aside className="account-side">
            <div className="account-user">
              <div className="avatar">{(user.name || 'G')[0].toUpperCase()}</div>
              <div>
                <strong>{user.name || 'Golden Taj Customer'}</strong>
                <p className="muted small">+91 {user.phone}</p>
                {user.userCode ? <span className="member-chip">★ {user.userCode}</span> : null}
              </div>
            </div>

            <div className="account-stats">
              <div><b>{orders.length}</b><span>Orders</span></div>
              <Link to="/wishlist"><b>{wishCount}</b><span>Wishlist</span></Link>
            </div>

            <nav className="account-nav">
              <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>
                <i className="fas fa-box" /> My Orders
              </button>
              <Link to="/rewards"><i className="fas fa-gift" /> Rewards &amp; Referrals</Link>
              <button className={tab === 'coupons' ? 'active' : ''} onClick={() => setTab('coupons')}>
                <i className="fas fa-ticket" /> Coupons &amp; Offers
              </button>
              <button className={tab === 'bulk' ? 'active' : ''} onClick={() => setTab('bulk')}>
                <i className="fas fa-truck-fast" /> Bulk Orders
              </button>
              <button className={tab === 'dealership' ? 'active' : ''} onClick={() => setTab('dealership')}>
                <i className="fas fa-handshake" /> Dealership Enquiry
              </button>
              <Link to="/wishlist"><i className="fas fa-heart" /> My Wishlist</Link>
              <button className={tab === 'addresses' ? 'active' : ''} onClick={() => setTab('addresses')}>
                <i className="fas fa-location-dot" /> Saved Addresses
              </button>
              <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>
                <i className="fas fa-user" /> Edit Profile
              </button>
              <button className={tab === 'tickets' ? 'active' : ''} onClick={() => setTab('tickets')}>
                <i className="fas fa-headset" /> Help &amp; Support
              </button>
              <button className="danger" onClick={() => { logout(); navigate('/'); }}>
                <i className="fas fa-right-from-bracket" /> Log Out
              </button>
            </nav>
          </aside>

          {/* Panel */}
          <div>
            {tab === 'orders' ? (
              <div className="panel-card">
                <h3>My Orders</h3>
                {orders.length === 0 ? (
                  <p className="muted">You haven't placed any orders yet.</p>
                ) : (
                  orders.map((o) => (
                    <Link className="order-row" key={o._id} to={`/order/${o._id}`}>
                      <div>
                        <strong>{o.orderNumber}</strong>
                        <p className="muted small">{fmt(o.createdAt)} · {o.items.length} item(s)</p>
                        <span className={`status ${o.status}`}>{String(o.status).replace(/_/g, ' ')}</span>
                      </div>
                      <div className="order-amt">
                        <strong>{inr(o.totalAmount)}</strong>
                        <span className="track-link"><i className="fas fa-angle-right" /> Track</span>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            ) : null}

            {tab === 'coupons' ? <CouponsTab /> : null}
            {tab === 'bulk' ? <EnquiryTab kind="bulk" /> : null}
            {tab === 'dealership' ? <EnquiryTab kind="dealership" /> : null}
            {tab === 'tickets' ? <TicketsTab /> : null}

            {tab === 'addresses' ? (
              <div className="panel-card">
                <div className="row-between">
                  <h3>Saved Addresses</h3>
                  <button className="link-btn inline" onClick={() => { setEditing(null); setShowForm(true); }}>+ Add new</button>
                </div>
                {addresses.length === 0 ? (
                  <p className="muted">No saved addresses yet.</p>
                ) : (
                  <div className="addr-list">
                    {addresses.map((a) => (
                      <div className="addr-option" key={a._id}>
                        <div style={{ flex: 1 }}>
                          <strong>
                            {a.fullName} <span className="addr-type">{a.type}</span>
                            {a.isDefault ? <span className="addr-type default">Default</span> : null}
                          </strong>
                          <p className="muted small">
                            {[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                          </p>
                          <p className="muted small">+91 {a.phone}</p>
                        </div>
                        <div className="addr-actions">
                          <button className="link-btn inline" onClick={() => { setEditing(a); setShowForm(true); }}>Edit</button>
                          <button className="link-btn inline danger" onClick={() => removeAddress(a._id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {tab === 'profile' ? (
              <div className="panel-card">
                <h3>Profile</h3>

                {incomplete ? (
                  <p className="form-msg err" style={{ textAlign: 'left', marginBottom: 14 }}>
                    <i className="fas fa-circle-info" /> Please complete your profile — we use your
                    name for deliveries and invoices.
                  </p>
                ) : null}

                <div className="form-row">
                  <div className="field">
                    <label>First Name *</label>
                    <input
                      value={profile.firstName}
                      onChange={(e) => setProfile((p) => ({ ...p, firstName: e.target.value }))}
                      placeholder="e.g. Aarav"
                    />
                  </div>
                  <div className="field">
                    <label>Last Name *</label>
                    <input
                      value={profile.lastName}
                      onChange={(e) => setProfile((p) => ({ ...p, lastName: e.target.value }))}
                      placeholder="e.g. Sharma"
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="field">
                  <label>Gender *</label>
                  <div className="gender-row">
                    {['male', 'female', 'other'].map((g) => (
                      <button
                        key={g}
                        className={profile.gender === g ? 'active' : ''}
                        onClick={() => setProfile((p) => ({ ...p, gender: g }))}
                      >
                        {g[0].toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <label>Mobile</label>
                  <input value={`+91 ${user.phone}`} readOnly />
                </div>

                {saved ? <p className={`form-msg ${saved.includes('updated') ? 'ok' : 'err'}`}>{saved}</p> : null}

                <button className="consult-btn" style={{ justifyContent: 'center' }} onClick={saveProfile}>
                  Save Profile
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {showForm ? (
        <AddressForm
          address={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); loadAddresses(); }}
        />
      ) : null}
    </>
  );
}
